import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { 
  Lock, 
  Play, 
  CheckCircle,
  ArrowRight,
  Clock
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';

const Courses = () => {
  const { user, isAuthenticated } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await api.get('/courses');
        setCourses(response.data);
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const tierColors = {
    basic: 'bg-zinc-800 text-zinc-400',
    premium: 'bg-white/10 text-white',
    elite: 'bg-[#D4AF37]/20 text-[#D4AF37]'
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="courses-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-2">
            Trading <span className="text-[#D4AF37]">Courses</span>
          </h1>
          <p className="text-zinc-400">Learn from expert traders with our comprehensive video courses</p>
        </motion.div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#121212] border border-white/5 animate-pulse">
                <div className="h-48 bg-zinc-800" />
                <div className="p-6">
                  <div className="h-6 bg-zinc-800 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-zinc-800 rounded w-full mb-2" />
                  <div className="h-4 bg-zinc-800 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, index) => (
              <motion.div
                key={course.course_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-[#121212] border border-white/5 overflow-hidden card-hover relative group ${
                  course.is_locked ? 'opacity-90' : ''
                }`}
                data-testid={`course-card-${course.course_id}`}
              >
                {/* Thumbnail */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={course.thumbnail_url || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400'}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  
                  {course.is_locked && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Lock className="w-12 h-12 text-[#D4AF37]" weight="duotone" />
                    </div>
                  )}
                  
                  {!course.is_locked && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-black ml-1" weight="fill" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-mono uppercase px-2 py-1 ${tierColors[course.tier_required] || tierColors.basic}`}>
                      {course.tier_required}
                    </span>
                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {course.modules?.length || 0} modules
                    </span>
                  </div>

                  <h3 className="font-outfit text-lg text-white mb-2 line-clamp-1">
                    {course.title}
                  </h3>
                  <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
                    {course.description}
                  </p>

                  {/* Modules Preview */}
                  <div className="space-y-2 mb-4">
                    {course.modules?.slice(0, 2).map((module, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                        <CheckCircle className="w-3 h-3 text-[#D4AF37]" weight="fill" />
                        <span className="truncate">{module.title}</span>
                      </div>
                    ))}
                    {course.modules?.length > 2 && (
                      <p className="text-xs text-zinc-600">+{course.modules.length - 2} more modules</p>
                    )}
                  </div>

                  {course.is_locked ? (
                    <Link to="/pricing">
                      <Button className="w-full bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                        Upgrade to Access
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <Link to={`/courses/${course.course_id}`}>
                      <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                        Start Course
                        <Play className="w-4 h-4 ml-2" weight="fill" />
                      </Button>
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Upgrade CTA */}
        {isAuthenticated && user?.subscription_tier === 'basic' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-gradient-to-r from-[#D4AF37]/20 to-[#B38F24]/20 border border-[#D4AF37]/30 p-8 text-center"
          >
            <h3 className="font-outfit text-2xl text-white mb-2">
              Unlock All Courses
            </h3>
            <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
              Upgrade to Premium or Elite to access all courses including advanced strategies and live trading sessions.
            </p>
            <Link to="/pricing">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] px-8 py-6 gold-glow">
                View Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Courses;
